import { EqlFieldValuesAutoComplete } from '@modules/common/components/EqlFieldValuesAutoComplete/EqlFieldValuesAutoComplete';
import { TaxonomyNodesGroupsAutoComplete } from '@modules/common/components/TaxonomyNodesAutoComplete/TaxonomyNodesGroupsAutoComplete';
import {
  SelectedTaxonomyNodeOption,
  TaxonomyNodeSuggestionOption,
} from '@modules/common/components/TaxonomyNodesAutoComplete/types';
import { ChevronLeftRounded } from '@mui/icons-material';
import {
  AutocompleteCloseReason,
  Box,
  BoxProps,
  Stack,
  TextFieldProps,
  Typography,
} from '@mui/material';
import { EqlFieldValue, LabelType } from 'generated/graphql';
import { ReactNode, SyntheticEvent, useState } from 'react';

export interface TaxonomyNodesAutoCompleteProps {
  isOpen?: boolean;
  className?: string;
  onClose?: () => void;
  onChange: (value: SelectedTaxonomyNodeOption) => void;
  openOnFocus?: boolean;
  onOpen?: () => void;
  renderInput?: (
    textFieldProps: TextFieldProps,
    activeOption?: TaxonomyNodeSuggestionOption,
  ) => ReactNode;
  renderOptionProp?: (
    optionProps: BoxProps,
    option: TaxonomyNodeSuggestionOption,
  ) => ReactNode;
  renderFooter?: () => ReactNode;
  onViewMoreChange?: (option: TaxonomyNodeSuggestionOption | null) => void;
}

export function TaxonomyNodesAutoComplete({
  isOpen,
  className,
  onClose,
  onChange,
  onOpen,
  openOnFocus = true,
  renderInput,
  renderOptionProp,
  renderFooter,
  onViewMoreChange,
}: TaxonomyNodesAutoCompleteProps) {
  const [inputValue, setInputValue] = useState('');
  const [shouldShowAllSuggestions, setShouldShowAllSuggestions] =
    useState(isOpen);

  const [activeViewMoreOption, setActiveViewMoreOption] =
    useState<TaxonomyNodeSuggestionOption | null>(null);

  const onViewMore = (option: TaxonomyNodeSuggestionOption) => {
    setActiveViewMoreOption(option);
    onViewMoreChange?.(option);
  };

  const showAllSuggestions = () => {
    setActiveViewMoreOption(null);
    onViewMoreChange?.(null);
    setShouldShowAllSuggestions(true);
  };

  const onInputChange = (_: SyntheticEvent, value: string) => {
    setInputValue(value);
  };

  const handleChange: TaxonomyNodesAutoCompleteProps['onChange'] = (value) => {
    onChange(value);
  };

  const handleAutoCompleteClose = (
    _: React.SyntheticEvent<Element, Event>,
    reason: AutocompleteCloseReason,
  ) => {
    if (reason === 'selectOption' && activeViewMoreOption) {
      return;
    }

    setShouldShowAllSuggestions(false);
    onClose?.();
  };

  if (!activeViewMoreOption) {
    return (
      <TaxonomyNodesGroupsAutoComplete
        fullWidth
        className={className}
        renderInput={renderInput}
        open={isOpen || shouldShowAllSuggestions}
        inputValue={inputValue}
        onInputChange={onInputChange}
        openOnFocus={openOnFocus}
        onChange={(value) => {
          handleChange({
            id: value.id,
            displayName: value.displayName,
            type: value.groupType,
            category: value.category,
          });
          setInputValue('');
        }}
        onClose={handleAutoCompleteClose}
        onViewMore={onViewMore}
        onOpen={onOpen}
        renderOptionProp={renderOptionProp}
        renderFooter={renderFooter}
      />
    );
  }

  if (activeViewMoreOption) {
    const onValueChange = (
      eqlFieldValue: EqlFieldValue | EqlFieldValue[] | null,
    ) => {
      if (Array.isArray(eqlFieldValue)) {
        return;
      }

      if (!eqlFieldValue) {
        return;
      }

      handleChange({
        id: eqlFieldValue.value,
        displayName: eqlFieldValue?.displayName,
        type: activeViewMoreOption.groupType,
      });
    };

    const getFieldFromLabelType = (label: LabelType) => {
      switch (label) {
        case LabelType.L1:
          return 'l1';
        case LabelType.Aspect:
          return 'entity';
        case LabelType.Reason:
          return 'reason';
        default:
          return '';
      }
    };

    return (
      <EqlFieldValuesAutoComplete
        open={isOpen}
        openOnFocus
        fullWidth
        onOpen={onOpen}
        onClose={(_, reason) => {
          if (reason === 'blur') {
            return;
          }
          onClose?.();
        }}
        inputVariant="outlined"
        inputValue={inputValue}
        onInputChange={onInputChange}
        onChange={onValueChange}
        multiple={false}
        filterField={getFieldFromLabelType(activeViewMoreOption.groupType)}
        fieldDisplayName={activeViewMoreOption.displayName}
        renderListFooter={renderFooter}
        shouldShowCategoryIcon
        renderListHeader={() => {
          return (
            <Box onMouseDown={(event) => event.preventDefault()}>
              <Stack
                direction="row"
                alignItems="center"
                onClick={showAllSuggestions}
                sx={{
                  p: 1,
                  pr: 2,
                  bgcolor: 'background.paper',
                  cursor: 'pointer',
                }}
              >
                <Box>
                  <ChevronLeftRounded
                    sx={{
                      color: 'text.secondary',
                    }}
                  />
                </Box>
                <Typography color="text.secondary">
                  Back to: All search suggestions...
                </Typography>
              </Stack>
            </Box>
          );
        }}
      />
    );
  }

  return null;
}
